import { Box, Button, Grow, IconButton, Typography, useTheme } from '@mui/material'
import { ChevronLeft, ChevronRight, PresentToAll } from '@mui/icons-material'
import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { observer } from 'mobx-react'

import { useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store'
import { useMeetingActionsContext } from '../../contexts/meetingactions.context'
import { useToggleButtonsData } from '../../hooks/useToggleButtonsData'
import { indexToName } from '../../utils/meeting-utilities'
import { useMeetingStore } from '../../store/meeting.store'
import VirtualboardManager from './VirtualboardManager'
import CustomSwitch from '../common/CustomSwitch'
import useWindow from '../../hooks/useWindow'
import { Icons } from '../../utils/utilities'
import UserCard from './UserCard'

const PAGE_SIZE_DESKTOP = 9
const PAGE_SIZE_MOBILE = 5


const sharingGridTemplates = {
  1: `"a"`,
  2: {
    md: `
    "a a a a a a b b"
    "a a a a a a b b"
    "a a a a a a b b"
    "a a a a a a b b"
    `,
    xs: `
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      ". b b b b ."
      ". b b b b ."
    `
  },
  3: {
    md: `
    "a a a a a a a b b"
    "a a a a a a a b b"
    "a a a a a a a c c"
    "a a a a a a a c c"
  `,
    sm: `
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      ". b b c c ."
      ". b b c c ."
    `,
    xs: `
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "b b b c c c"
      "b b b c c c"
    `
  },
  4: {
    md: `
    "a a a a b"
    "a a a a c"
    "a a a a d"
  `,
    xs: `
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "b b c c d d"
    `
  },
  5: {
    md: `
    "a a a a a a a a b b"
    "a a a a a a a a c c"
    "a a a a a a a a d d"
    "a a a a a a a a e e"
  `,
    xs: `
      "a a a a"
      "a a a a"
      "a a a a"
      "b b c c"
      "d d e e"
    `
  },
  6: {
    xs: `
    "a a a a a a"
    "a a a a a a"
    "a a a a a a"
    "b b c c e e"
    "b b d d f f"
  `, md: `
    "a a a a a b c"
    "a a a a a d e"
    "a a a a a f f"
  `},
  7: {
    xs: `
    "a a a a a a"
    "a a a a a a"
    "a a a a a a"
    "b b d d f f"
    "c c e e g g"
  `,
    md: `
    "a a a a a b c"
    "a a a a a d e"
    "a a a a a f g"
  `
  },
  8: {
    md: `
    "a a a a a b c"
    "a a a a a d e"
    "a a a a a f g"
    "a a a a a h h"
  `, xs: `
    "a a a a"
    "a a a a"
    "a a a a"
    "b d f g"
    "c e h h"
  `}
}

function sortUsers ( users ) {
  return [...users].sort( ( a, b ) => {
    // weight function gives priority score

    const weight = ( u ) => {

      if ( u.type === 'local-client' ) return 1
      if ( u.activePresenter ) return 70
      if ( u.isSharing ) return 60
      if ( u.isVideoOn ) return 50
      if ( u.isAudioOn ) return 40
      if ( u.pinned ) return 30
      return 20
    }

    return weight( b ) - weight( a ) // higher weight first
  } )
}

const WebinarMeetingParticipationLayout = observer( () => {

  const MeetingStore = useMeetingStore()
  const { width: windowWidth } = useWindow()

  const { palette, border } = useTheme()
  const { meeting_id } = useParams()

  const { toggleScreenShare, toggleScreenShareAudio } = useMeetingActionsContext()
  const { raiseHandButtonDetails } = useToggleButtonsData()

  // const { socket, emitEvent } = useSocketsContext()
  const OnlineSessionSocketStore = useOnlineSessionsSocketStore()

  const [currentPage, setCurrentPage] = useState( 0 )
  const [showPagination, setShowPagination] = useState( false )
  const pageSize = windowWidth < 600 ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP

  const raiseHand = async () => {
    if ( MeetingStore.getMeetingState.raisedHand ) {
      const { success, message } = await OnlineSessionSocketStore.emit( "handraises:lower", { room_id: meeting_id } )
      if ( success )
        MeetingStore.setMeetingState( { raisedHand: false } )
      if ( message )
        toast( message )
      return
    }

    const { success, message } = await OnlineSessionSocketStore.emit( "handraises:raise", { room_id: meeting_id } )

    if ( success )
      MeetingStore.setMeetingState( { raisedHand: true } )
    if ( message )
      toast( message )

  }

  const getUsersCard = useMemo( () => {
    const data = {}
    const filtered = [...MeetingStore.getRemoteUsers, MeetingStore.getLocalUser].filter( user => "share-client" === user.type || user.isVideoOn || user.isAudioOn || user.pinned )
    const sortedUsers = sortUsers( filtered )

    data.presentingVirtualBoard = MeetingStore.getMeetingState.presentingVirtualBoard
    data.showEmptyBoard = !( sortedUsers[0] && ( sortedUsers[0]?.sharing || sortedUsers[0]?.type === 'share-client' ) ) && !data.presentingVirtualBoard
    data.sharingLayout = MeetingStore.getMeetingState.isSharing || MeetingStore.getMeetingState.isSomeoneSharing || Boolean( MeetingStore.getMeetingState?.activePresenter )
    data.isActivePresenter = MeetingStore.getMeetingState?.activePresenter?.user_id === `${MeetingStore.getLocalUser.user_id}-sharing`
    data.activePresenter = MeetingStore.getMeetingState?.activePresenter

    // Slot "a" is always reserved: either by the fixed board/empty-state UI, or — when neither is shown —
    // by the top-priority user (the sharer), who then stays pinned to "a" across every page.
    const aSlotIsFixedUI = data.showEmptyBoard || data.presentingVirtualBoard
    const pinnedTopUser = aSlotIsFixedUI ? null : sortedUsers[0]
    const usersToPaginate = pinnedTopUser ? sortedUsers.slice( 1 ) : sortedUsers

    const chunkSize = Math.max( 1, pageSize - 1 )
    data.totalPages = Math.max( 1, Math.ceil( usersToPaginate.length / chunkSize ) )
    data.page = Math.min( currentPage, data.totalPages - 1 )

    data.pinnedTopUser = pinnedTopUser
    data.users = usersToPaginate.slice( data.page * chunkSize, data.page * chunkSize + chunkSize )
    data.cards = 1 + data.users.length

    data.areasLayout = sharingGridTemplates[data.cards]
    return data
  }, [MeetingStore.getMeetingState, MeetingStore.getRemoteUsers, MeetingStore.getLocalUser, pageSize, currentPage] )


  return (
    <Box minHeight={0} flexShrink={1} display="flex" flexDirection="column" id="users-container" flexGrow={1} gap="10px" >
      {getUsersCard.sharingLayout && getUsersCard.activePresenter && <Box borderRadius="10px" bgcolor={palette.rowHover} padding="10px 15px">
        {getUsersCard.isActivePresenter && <Box color={palette.greyed.contrastText} display="flex" justifyContent="space-between" alignItems="center" gap="10px">
          <Box display="flex" alignItems="center" gap="10px">
            <PresentToAll sx={{ color: "inherit" }} fontSize='small' />
            <Typography variant='subtitle2' sx={{ color: palette.greyed.contrastText }} fontSize="12px">You are presenting</Typography>
          </Box>
          {MeetingStore.getMeetingState.sharingState.audio && <Box display="flex" alignItems="center" gap="10px">
            <Typography variant='subtitle2' sx={{ color: palette.greyed.contrastText }} fontSize="12px">Share audio</Typography>
            <CustomSwitch color="secondary" size="small" checked={MeetingStore.getMeetingState.sharingState.audioEnabled} onChange={event => {
              toggleScreenShareAudio( event.target.checked )
            }} />
          </Box>}
        </Box>}
        {!getUsersCard.isActivePresenter && <Box>
          <Typography variant='subtitle2' sx={{ color: palette.greyed.contrastText }} fontSize="12px" display="block" >{getUsersCard?.activePresenter?.name || getUsersCard?.activePresenter?.user_name} is presenting</Typography>
        </Box>}
      </Box>}
      {MeetingStore.getMeetingState.isJoined && <Box minHeight={0} display="flex" flexGrow={1} flexDirection={MeetingStore.getMeetingState.isSharing ? "row" : "column"}  >
        <Box
          id="users-grid"
          display="grid"
          gridTemplateAreas={
            getUsersCard.areasLayout
          }
          gridAutoRows="1fr"
          gridAutoColumns="1fr"
          gap="10px"
          flexGrow={1}
          overflow="auto"
          sx={{
            transition: `all 1s ease`
          }}
        >
          {getUsersCard.presentingVirtualBoard && MeetingStore.getPresentingDocument?.slides && <Box bgcolor="grey" borderRadius="20px" gridArea="a">
            <VirtualboardManager />
          </Box>}
          {getUsersCard.showEmptyBoard && <Box borderRadius="20px" border={border[1]} bgcolor={palette.contentBg + "66"} padding="20px" display="flex" flexDirection="column" gap="10px" alignItems="center" justifyContent="center" sx={{ gridArea: "a" }} >
            <Typography textAlign="center" component="h1" fontSize="1.4rem" fontWeight="600" >Nothing is being shared right now!</Typography>
            <Typography textAlign="center" fontSize="12px" component="span" maxWidth="450px">
              {MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id ) ? "Start sharing your sceen to present to the participants." : "Please wait for the meeting host to share something, or you can ask permission to share by raising your hand."}
            </Typography>
            <Box display="flex" marginTop="10px" justifyContent="center" gap="10px" flexWrap="wrap" alignItems="center">
              {MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id ) && <Button variant='outlined' sx={{ flexGrow: 1, borderRadius: "20px" }} color='secondary' onClick={toggleScreenShare} startIcon={<PresentToAll />}>Start Sharing</Button>}
              {MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id ) && <Button variant='outlined' sx={{ flexGrow: 1, borderRadius: "20px" }} color='secondary' onClick={() => MeetingStore.setMeetingRoomLayoutSettings( { isPrentationsManagerOpen: true } )} startIcon={Icons.default.FilePresent}>Start Presenting File</Button>}
              {!MeetingStore.getCurrentMeeting?.hosts?.find( u => u.user_id === MeetingStore.getLocalUser.user_id ) && <Button variant='outlined' sx={{ flexGrow: 1, borderRadius: "20px" }} color='greyed' onClick={raiseHand} startIcon={raiseHandButtonDetails.icon}>{raiseHandButtonDetails.isOn ? "Lower hand" : "Raise Hand"}</Button>}
            </Box>
          </Box>}
          {getUsersCard.pinnedTopUser && <UserCard toggleScreenShare={toggleScreenShare} gridArea="a" key={getUsersCard.pinnedTopUser.user_id} user={getUsersCard.pinnedTopUser} />}
          {getUsersCard.users.map( ( user, index ) => (
            <UserCard toggleScreenShare={toggleScreenShare} gridArea={indexToName( index + 1 )} key={user.user_id} user={user} />
          )
          )}
        </Box>
      </Box>}
      <Box sx={{ maxHeight: getUsersCard.totalPages > 1 ? 80 : 0, overflow: "hidden", display: "flex", alignItems: "center" }} >
        <Box component={Grow} in={getUsersCard.totalPages > 1}>
          <Box flexShrink={0} display="flex" alignItems="center" justifyContent="center" gap="8px">
            <IconButton size="small" disabled={getUsersCard.page === 0} onClick={() => setCurrentPage( p => Math.max( 0, p - 1 ) )}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="textSecondary">Group {getUsersCard.page + 1} / {getUsersCard.totalPages}</Typography>
            <IconButton size="small" disabled={getUsersCard.page === getUsersCard.totalPages - 1} onClick={() => setCurrentPage( p => Math.min( getUsersCard.totalPages - 1, p + 1 ) )}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  )
} )

export default WebinarMeetingParticipationLayout
